import { ActionContext, ActionTree } from 'vuex';
import LagerService from '@/modules/ewerp/service/LagerService';
import ApiService from '@/modules/ewerp/service/LagerService';

import { RootState } from '@/modules/ewerp/store';
import { useEwerpCommonStore } from '@/modules/ewerp/store/ewerpCommonStore';

import { State } from './state';

import { Mutations, MutationTypes } from './mutations';


import { Printdata } from '@/modules/ewerp/models/PrintdataModels';
import { Email } from '@/modules/ewerp/models/CommonModels';

export enum ActionTypes {
  GetPrintdataFileList = 'GETPRINDATAFILELIST',
  DownloadPrintdataFile = 'DOWNLOADPRINDDATAFILE',
  UploadPrintdataFile = 'UPLOADPRINDDATAFILE',
  DeletePrintdataFile = 'DELETEPRINDDATAFILE',
  SendPrintdataEmail = 'SENDPRINTDATAEMAIL',
}

type ActionArguments = Omit<ActionContext<State, RootState>, 'commit'> & {
  commit<K extends keyof Mutations>(key: K, payload: Parameters<Mutations[K]>[1]): ReturnType<Mutations[K]>;
};

export type Actions = {
  [ActionTypes.GetPrintdataFileList](context: ActionArguments): Promise<void>;
  [ActionTypes.DownloadPrintdataFile](context: ActionArguments, fileName: string): Promise<void>;
  [ActionTypes.UploadPrintdataFile](context: ActionArguments, file: FormData): Promise<void>;
  [ActionTypes.DeletePrintdataFile](context: ActionArguments, fileName: string): Promise<void>;
  [ActionTypes.SendPrintdataEmail](context: ActionArguments, email: Email): Promise<void>;
};

export const actions: ActionTree<State, RootState> & Actions = {
  async [ActionTypes.GetPrintdataFileList]({ commit }) {
    const commonStore = useEwerpCommonStore();
    const status = commonStore.status;
    status.printdataFileListLoading = true;
    commonStore.setStatus(status);

    return LagerService.getPrintdate()
      .then((res) => {
        status.printdataFileListLoading = false;
        commonStore.setStatus(status);

        const result: Array<Printdata> = res.data;
        commit(MutationTypes.GetPrintdataFilelistSuccess, result);
      })
      .catch((error) => {
        status.printdataFileListLoading = false;
        // console.log(error);
        if (error.message === 'Network Error') {
          status.printdataFileListErrorMsg = error.message;
        } else if (error.response?.status == 401) {
          status.printdataFileListErrorMsg = 'Sie haben keinen Zugriff auf diese Daten';
        } else {
          status.printdataFileListErrorMsg = error;
        }

        sessionStorage.removeItem('printdata');
        commonStore.setStatus(status);
      });
  },

  async [ActionTypes.DownloadPrintdataFile]({  }, fileName) {
    const commonStore = useEwerpCommonStore();
    const status = commonStore.status;
    status.printdataLoading = true;
    commonStore.setStatus(status);

    return LagerService.downloadPrintdate(fileName)
      .then((res) => {
        status.printdataLoading = false;
        commonStore.setStatus(status);

        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
      })
      .catch((error) => {
        status.printdataLoading = false;
        // console.log(error);
        if (error.message === 'Network Error') {
          status.printdataErrorMsg = error.message;
        } else if (error.response?.status == 401) {
          status.printdataErrorMsg = 'Sie haben keinen Zugriff auf diese Daten';
        } else {
          status.printdataErrorMsg = error;
        }

        commonStore.setStatus(status);
      });
  },

  async [ActionTypes.UploadPrintdataFile]({ dispatch }, file) {
    const commonStore = useEwerpCommonStore();
    const status = commonStore.status;
    status.printdataLoading = true;
    commonStore.setStatus(status);

    return LagerService.uploadPrintdate(file)
      .then(() => {
        status.printdataLoading = false;
        commonStore.setStatus(status);

        dispatch(ActionTypes.GetPrintdataFileList);
      })
      .catch((error) => {
        status.printdataLoading = false;
        // console.log(error);
        if (error.message === 'Network Error') {
          status.printdataErrorMsg = error.message;
        } else if (error.response?.status == 401) {
          status.printdataErrorMsg = 'Sie haben keinen Zugriff auf diese Daten';
        } else {
          status.printdataErrorMsg = error;
        }

        commonStore.setStatus(status);
      });
  },

  async [ActionTypes.DeletePrintdataFile]({ commit }, fileName) {
    const commonStore = useEwerpCommonStore();
    const status = commonStore.status;
    status.printdataLoading = true;
    commonStore.setStatus(status);

    return LagerService.deletePrintdate(fileName)
      .then(() => {
        commit(MutationTypes.DeletePrintdataFileSuccess, fileName);
        status.printdataLoading = false;
        commonStore.setStatus(status);
      })
      .catch((error) => {
        status.printdataLoading = false;
        // console.log(error);
        if (error.message === 'Network Error') {
          status.printdataErrorMsg = error.message;
        } else if (error.response?.status == 401) {
          status.printdataErrorMsg = 'Sie haben keinen Zugriff auf diese Daten';
        } else {
          status.printdataErrorMsg = error;
        }

        commonStore.setStatus(status);
      });
  },

  async [ActionTypes.SendPrintdataEmail]({  }, email) {
    const commonStore = useEwerpCommonStore();
    const status = commonStore.status;
    status.printdataLoading = true;
    commonStore.setStatus(status);

    return ApiService.sendEmail(email)
      .then(() => {
        status.printdataLoading = false;
        commonStore.setStatus(status);
      })
      .catch((error) => {
        status.printdataLoading = false;
        // console.log(error);
        if (error.message === 'Network Error') {
          status.printdataErrorMsg = error.message;
        } else if (error.response?.status == 401) {
          status.printdataErrorMsg = 'Sie haben keinen Zugriff auf diese Daten';
        } else {
          status.printdataErrorMsg = error;
        }

        commonStore.setStatus(status);
      });
  },
};
